import React, { useEffect, useMemo, useRef, useState } from "react";
import phoneCodes from "../../screens/Register/PhoneCodes.json";
import "./PhoneNumberField.css";

const DEFAULT_DIAL_CODE = "+1";

export const PHONE_CODE_OPTIONS = phoneCodes.map(({ code, label }) => ({
  code: normalizeDialCode(code),
  label,
  searchValue: `${normalizeDialCode(code)} ${label}`.toLowerCase(),
}));

function getOptionByCode(code) {
  const normalizedCode = normalizeDialCode(code);
  return PHONE_CODE_OPTIONS.find((option) => option.code === normalizedCode) || null;
}

export function normalizeDialCode(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits ? `+${digits}` : DEFAULT_DIAL_CODE;
}

export function normalizePhoneDigits(value) {
  return String(value ?? "").replace(/\D/g, "");
}

export function buildPhoneNumber(dialCode, phoneDigits) {
  const normalizedDigits = normalizePhoneDigits(phoneDigits);
  if (!normalizedDigits) return "";
  return `${normalizeDialCode(dialCode)}${normalizedDigits}`;
}

export function isValidPhoneNumber(dialCode, phoneDigits) {
  const fullPhoneNumber = buildPhoneNumber(dialCode, phoneDigits);
  return /^\+?[0-9]{6,15}$/.test(fullPhoneNumber);
}

export function splitPhoneNumber(phoneNumber) {
  const normalizedValue = String(phoneNumber ?? "").trim();
  if (!normalizedValue) {
    return {
      dialCode: DEFAULT_DIAL_CODE,
      phoneDigits: "",
    };
  }

  const fullPhoneNumber = normalizedValue.startsWith("+")
    ? `+${normalizedValue.slice(1).replace(/\D/g, "")}`
    : `+${normalizedValue.replace(/\D/g, "")}`;

  const matchedOption = [...PHONE_CODE_OPTIONS]
    .sort((left, right) => right.code.length - left.code.length)
    .find((option) => fullPhoneNumber.startsWith(option.code));

  if (!matchedOption) {
    return {
      dialCode: DEFAULT_DIAL_CODE,
      phoneDigits: fullPhoneNumber.replace(/^\+/, ""),
    };
  }

  return {
    dialCode: matchedOption.code,
    phoneDigits: fullPhoneNumber.slice(matchedOption.code.length),
  };
}

function PhoneNumberField({
  dialCode,
  phoneDigits,
  onDialCodeChange,
  onPhoneDigitsChange,
  phonePlaceholder = "Phone digits",
  searchPlaceholder = "Search country or code",
}) {
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectedOption = useMemo(
    () => getOptionByCode(dialCode) || getOptionByCode(DEFAULT_DIAL_CODE),
    [dialCode]
  );

  const filteredOptions = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return PHONE_CODE_OPTIONS;
    }

    return PHONE_CODE_OPTIONS.filter((option) => option.searchValue.includes(query));
  }, [searchValue]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setSearchValue("");
    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [isOpen]);

  return (
    <div className="phone-number-field" ref={containerRef}>
      <div className="phone-code-picker">
        <button
          type="button"
          className="phone-code-trigger"
          onClick={() => setIsOpen((current) => !current)}
          aria-expanded={isOpen}
          aria-label="Select country code"
          title={selectedOption?.label || "Country code"}
        >
          <span className="phone-code-value">{selectedOption?.code || DEFAULT_DIAL_CODE}</span>
          <span className="phone-code-chevron" aria-hidden="true">v</span>
        </button>

        {isOpen && (
          <div className="phone-code-dropdown">
            <input
              ref={searchInputRef}
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={searchPlaceholder}
              className="phone-code-search"
            />

            <div className="phone-code-options" role="listbox" aria-label="Country codes">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={`${option.code}-${option.label}`}
                    type="button"
                    className={`phone-code-option${
                      option.code === selectedOption?.code ? " is-selected" : ""
                    }`}
                    onClick={() => {
                      onDialCodeChange(option.code);
                      setIsOpen(false);
                    }}
                  >
                    <span className="phone-code-option-code">{option.code}</span>
                    <span className="phone-code-option-label">{option.label}</span>
                  </button>
                ))
              ) : (
                <p className="phone-code-empty">No matching country code.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <input
        type="tel"
        inputMode="numeric"
        value={phoneDigits}
        onChange={(event) => onPhoneDigitsChange(normalizePhoneDigits(event.target.value))}
        placeholder={phonePlaceholder}
        className="phone-number-input"
      />
    </div>
  );
}

export default PhoneNumberField;
